// components/Layout.js
import React, { useContext, useState, useEffect, Suspense } from 'react';
import { Outlet, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  AcademicCapIcon,
  UserGroupIcon,
  UserIcon,
  FolderIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  CogIcon,
  Bars3Icon,
  BookOpenIcon,
  ChartBarIcon,
  BanknotesIcon,
  UsersIcon,
  XMarkIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { Badge } from './ui';

const Layout = () => {
  const { logout, hasFilledSchoolInfo, isAuthenticated } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const {
    hasPermission,
    isAdmin,
    isSuperviseur,
    isComptable,
    isGestionnairePedagogique,
    isGestionnaireRH,
    user
  } = usePermissions();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setSidebarOpen(!mobile);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    const confirmLogout = window.confirm(t('dashboard.confirmLogout') || 'Voulez-vous vous déconnecter ?');
    if (confirmLogout) {
      logout();
      navigate('/login');
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    // La sauvegarde localStorage se fait automatiquement dans i18n.js
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Rôle principal de l'utilisateur, exprimé en ton sémantique (pas en classes)
  const getUserRole = () => {
    if (!user || !user.roles || user.roles.length === 0) {
      return { name: 'Utilisateur', tone: 'neutral' };
    }

    const role = user.roles[0];
    const roleName = role.name || role.slug || 'Utilisateur';

    const roleTones = {
      'administrateur': 'brand',
      'Administrateur': 'brand',
      'superviseur': 'info',
      'Superviseur': 'info',
      'comptable': 'success',
      'Comptable': 'success',
      'gestionnaire-rh': 'warning',
      'Gestionnaire RH': 'warning',
      'gestionnaire-pedagogique': 'info',
      'Gestionnaire Pédagogique': 'info'
    };

    // Le nom du rôle vient de la base en français : roleNames.* le traduit
    const translationKey = `roleNames.${roleName}`;
    const translated = t(translationKey);
    return {
      name: translated === translationKey ? roleName : translated,
      tone: roleTones[role.slug] || roleTones[roleName] || 'neutral',
    };
  };

  const userRole = getUserRole();

  // Fonction pour vérifier si un élément doit être visible selon les restrictions par rôle
  const isItemVisible = (item) => {
    // Restrictions pour Comptable
    if (isComptable && isComptable()) {
      // Le comptable ne peut pas voir: calendrier, statistiques, paramètres, présence
      const comptableBlockedItems = ['/calendar', '/statistics', '/settings', '/attendance'];
      if (comptableBlockedItems.includes(item.link)) {
        return false;
      }
    }
    
    // Restrictions pour Gestionnaire Pédagogique
    if (isGestionnairePedagogique && isGestionnairePedagogique()) {
      // Le gestionnaire pédagogique ne peut pas voir: frais, calendrier
      const pedagogiqueBlockedItems = ['/fees', '/calendar'];
      if (pedagogiqueBlockedItems.includes(item.link)) {
        return false;
      }
    }
    
    // Restrictions pour Gestionnaire RH
    if (isGestionnaireRH && isGestionnaireRH()) {
      // Le gestionnaire RH ne peut pas voir: statistiques, paramètres
      const rhBlockedItems = ['/statistics', '/settings'];
      if (rhBlockedItems.includes(item.link)) {
        return false;
      }
    }
    
    // Restrictions pour Superviseur
    if (isSuperviseur && isSuperviseur()) {
      // Le superviseur peut voir seulement: dashboard, élèves (pour accéder aux présences), présences
      const superviseurAllowedItems = ['/dashboard', '/students', '/attendance'];
      if (!superviseurAllowedItems.includes(item.link)) {
        return false;
      }
    }
    
    return true;
  };

  // Tous les éléments possibles du menu
  const allMenuItems = [
    { title: t('dashboard.dashboard'), icon: AcademicCapIcon, link: '/dashboard', always: true },
    { title: t('dashboard.students'), icon: UserGroupIcon, link: '/students', permission: 'students.read' },
    { title: t('dashboard.teachers'), icon: UserIcon, link: '/teachers', permission: 'teachers.read' },
    { title: t('dashboard.classes'), icon: FolderIcon, link: '/classes', permission: 'classes.read' },
    { title: t('dashboard.subjects'), icon: BookOpenIcon, link: '/subjects', permission: 'classes.read' },
    { title: t('dashboard.fees'), icon: CurrencyDollarIcon, link: '/fees', permission: 'payments.read' },
    { title: t('dashboard.expenses') || 'Dépenses', icon: BanknotesIcon, link: '/expenses', permission: 'administration.manage' },
    { title: t('dashboard.bulletins'), icon: AcademicCapIcon, link: '/bulletins', permission: 'bulletins.read' },
    { title: t('dashboard.calendar'), icon: CalendarIcon, link: '/calendar', always: true },
    { title: t('dashboard.attendance'), icon: UserGroupIcon, link: '/attendance', permission: 'students.read' },
    { title: t('dashboard.statistics'), icon: ChartBarIcon, link: '/statistics', always: true },
    { title: t('dashboard.userManagement'), icon: UsersIcon, link: '/admin/users', permission: 'users.read' },
    { title: t('dashboard.settings'), icon: CogIcon, link: '/settings', always: true },
  ];

  // Créer la liste des éléments du sidebar selon les permissions et restrictions de rôles
  const sidebarItems = allMenuItems.filter(item => {
    // D'abord vérifier les restrictions par rôle
    if (!isItemVisible(item)) {
      return false;
    }
    
    // Ensuite vérifier les permissions
    if (isAdmin && isAdmin()) {
      return true; // Les admins voient tout (sauf restrictions)
    }
    
    return item.always || // Éléments toujours visibles
           (item.permission && hasPermission && hasPermission(item.permission)); // Éléments avec permissions
  });

  // Tant que l'école n'est pas configurée, l'app n'est pas utilisable :
  // on emmène l'administrateur sur la page de configuration au lieu de
  // poser une modale au-dessus d'un tableau de bord vide.
  if (isAuthenticated && !hasFilledSchoolInfo) {
    return <Navigate to="/school-setup" replace />;
  }

  const showLabels = isMobile || sidebarOpen;
  const currentItem = sidebarItems.find((item) => location.pathname.startsWith(item.link));

  return (
    <div className="flex h-screen overflow-hidden bg-canvas" dir={i18n.dir()}>
      {/* Voile derrière le tiroir mobile */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-ink/40 transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Navigation latérale : tiroir sur mobile, rail repliable sur desktop */}
      <aside
        className={`flex shrink-0 flex-col border-e border-line bg-surface transition-all duration-200 ${
          isMobile
            ? `fixed inset-y-0 start-0 z-40 w-64 shadow-pop ${sidebarOpen ? 'flex' : 'hidden'}`
            : `relative ${sidebarOpen ? 'w-60' : 'w-16'}`
        }`}
      >
        <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-line px-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand text-white">
            <AcademicCapIcon className="h-5 w-5" />
          </span>
          {showLabels && (
            <span className="truncate text-sm font-semibold tracking-tight text-ink">
              {t('dashboard.title')}
            </span>
          )}
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(false)}
              className="ms-auto rounded-md p-1.5 text-ink-subtle hover:bg-surface-hover hover:text-ink"
              aria-label={t('close') || 'Fermer'}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        <nav className="scrollbar-slim flex-1 space-y-0.5 overflow-y-auto p-2">
          {sidebarItems.map((item) => {
            const active = location.pathname.startsWith(item.link);
            return (
              <Link
                key={item.link}
                to={item.link}
                title={!showLabels ? item.title : undefined}
                className={`flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors duration-150 ${
                  active
                    ? 'bg-brand-subtle text-brand-ink'
                    : 'text-ink-muted hover:bg-surface-hover hover:text-ink'
                } ${showLabels ? '' : 'justify-center'}`}
                onClick={() => {
                  if (isMobile) setSidebarOpen(false);
                }}
              >
                <item.icon className="h-5 w-5 shrink-0" />
                {showLabels && <span className="truncate">{item.title}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="shrink-0 border-t border-line p-2">
          <button
            onClick={handleLogout}
            title={!showLabels ? t('dashboard.logout') : undefined}
            className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium text-ink-muted transition-colors duration-150 hover:bg-danger-subtle hover:text-danger ${
              showLabels ? '' : 'justify-center'
            }`}
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5 shrink-0 rtl:rotate-180" />
            {showLabels && <span>{t('dashboard.logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Colonne principale */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-line bg-surface px-3 sm:px-4">
          <div className="flex min-w-0 items-center gap-2">
            <button
              onClick={toggleSidebar}
              className="rounded-md p-1.5 text-ink-subtle transition-colors hover:bg-surface-hover hover:text-ink"
              aria-label="Menu"
            >
              <Bars3Icon className="h-5 w-5" />
            </button>
            <h1 className="truncate text-sm font-semibold text-ink">
              {currentItem ? currentItem.title : t('dashboard.title')}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <Badge tone={userRole.tone} className="hidden sm:inline-flex">
              {userRole.name}
            </Badge>

            {/* Sélecteur de langue segmenté */}
            <div className="flex items-center rounded-lg border border-line p-0.5">
              {['fr', 'ar'].map((lng) => (
                <button
                  key={lng}
                  onClick={() => changeLanguage(lng)}
                  className={`rounded-md px-2 py-1 text-2xs font-semibold uppercase transition-colors ${
                    i18n.language.startsWith(lng)
                      ? 'bg-brand-subtle text-brand-ink'
                      : 'text-ink-subtle hover:text-ink'
                  }`}
                >
                  {lng}
                </button>
              ))}
            </div>

            <button
              onClick={handleLogout}
              className="rounded-md p-1.5 text-ink-subtle transition-colors hover:bg-danger-subtle hover:text-danger sm:hidden"
              aria-label={t('dashboard.logout')}
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 rtl:rotate-180" />
            </button>
          </div>
        </header>

        <main className="scrollbar-slim flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6">
            <Suspense fallback={<div className="flex min-h-[50vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-brand" /></div>}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>

    </div>
  );
};

export default Layout;
